import * as React from "react";
import {
  Box,
  Container,
  Text,
  Stack,
  Select,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import BooLogo from "../../images/boo.png";
import DrDaoLogo from "../../images/drdao.png";
import Web3Service from "../../core/web3.service";
import Collections, { CollectionType } from "../../core/Collections";
import { merge, tap } from "rxjs";
import { GOG_ADDRESS } from "../../config";
import NFTAlertDialog from "./NFTAlertDialog";

interface NftProps {
  id: number;
  url: string;
}

const NFTStakingView: React.FC = () => {
  const web3Service: Web3Service = Web3Service.shared();

  // State Variables
  const [stakedNFTs, SetStakedNFTs] = React.useState<number[]>([]);
  const [myNFTs, SetMyNFTs] = React.useState<any[]>([]);
  const [selectedCollection, SetSelectedCollection] =
    React.useState<CollectionType>(Collections[0]);
  const [rewardRate, SetRewardRate] = React.useState<number | undefined>(
    undefined,
  );
  const [rewards, SetRewards] = React.useState<number | undefined>(undefined);
  const [remainingTime, SetRemainingTime] = React.useState("");
  const [account, setAccount] = React.useState<string | undefined>(undefined);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const {
    onOpen: onWithdrawOpen,
    onClose: onWithdrawClose,
    isOpen: isWithdrawOpen,
  } = useDisclosure();
  const leastDestructiveRef = React.useRef<HTMLButtonElement>(null);
  const [selectedTokens, setSelectedTokens] = React.useState<number[]>([]);
  const base = "https://";
  const [idToNft, setIdToNft] = React.useState<NftProps[]>([]);
  const [idToWithdraw, setIdToWithdraw] = React.useState<NftProps[]>([]);

  // Function to fetch NFT data for a given set of tokens
  const fetchNFTData = async (tokens: number[]) => {
    if (tokens.length > 0) {
      setIdToNft([]);
      tokens.forEach((tokenId) => {
        setIdToNft((prev) => [
          ...prev,
          {
            id: Number(tokenId),
            url:
              base +
              selectedCollection.baseUrl +
              ".ipfs.dweb.link/" +
              tokenId +
              ".png",
          },
        ]);
      });

      // Sort NFTs by ID
      setIdToNft((val) =>
        val.sort((a, b) => {
          return a.id - b.id;
        }),
      );
      console.log(idToNft);
    } else {
      setIdToNft([]);
    }
  };

  // Function to fetch NFT data for a given set of tokens
  const fetchNFTDataWithdraw = async (tokens: number[]) => {
    if (tokens.length > 0) {
      setIdToWithdraw([]);
      tokens.forEach((tokenId) => {
        setIdToWithdraw((prev) => [
          ...prev,
          {
            id: Number(tokenId),
            url:
              base +
              selectedCollection.baseUrl +
              ".ipfs.dweb.link/" +
              tokenId +
              ".png",
          },
        ]);
      });

      // Sort NFTs by ID
      setIdToWithdraw((val) =>
        val.sort((a, b) => {
          return a.id - b.id;
        }),
      );
    } else {
      setIdToWithdraw([]);
    }
  };

  // Function to close the AlertDialog and clear selected tokens
  const handleClose = () => {
    onClose();
    setSelectedTokens([]);
  };

  const handleWithdrawClose = () => {
    onWithdrawClose();
    setSelectedTokens([]);
  };

  React.useEffect(() => {
    if (selectedCollection) {
      // Fetch various data from the web3 service
      web3Service.getStakedNfts(
        selectedCollection.collectionAddress,
        isGhostOG(selectedCollection.collectionAddress),
      );
      web3Service.getUserTokens(selectedCollection.collectionAddress);
      web3Service.getRewardRate(
        isGhostOG(selectedCollection.collectionAddress),
      );
      web3Service.getUserReward(
        selectedCollection.collectionAddress,
        isGhostOG(selectedCollection.collectionAddress),
      );
      web3Service.getRewardDuration(
        isGhostOG(selectedCollection.collectionAddress),
      );

      // Observables to handle data updates
      const stakedTokens$ = web3Service.stakedNfts$.pipe(
        tap((tokens) => {
          SetStakedNFTs(tokens);
          fetchNFTDataWithdraw(tokens);
        }),
      );

      const account$ = web3Service.account$.pipe(
        tap((account) => {
          setAccount(account);
        }),
      );

      const ownedTokens$ = web3Service.tokens$.pipe(
        tap((tokens) => {
          SetMyNFTs(tokens);
          fetchNFTData(tokens);
        }),
      );

      const rewardForCollection$ = web3Service.rewards$.pipe(
        tap((reward) => {
          SetRewards(reward);
        }),
      );

      const rewardPerTokenRate$ = web3Service.stakedTokenRate$.pipe(
        tap((rate) => {
          SetRewardRate(rate);
        }),
      );

      const rewardDuration$ = web3Service.rewardDuration$.pipe(
        tap((duration) => {
          const currentTimestamp = Math.floor(Date.now() / 1000); // Current timestamp in seconds
          const remainingTime = Math.max(0, duration! - currentTimestamp);
          const days = Math.floor(remainingTime / (60 * 60 * 24));
          const hours = Math.floor(
            (remainingTime % (60 * 60 * 24)) / (60 * 60),
          );
          const minutes = Math.floor((remainingTime % (60 * 60)) / 60);

          SetRemainingTime(`${days} days, ${hours} hours, ${minutes} minutes`);
        }),
      );
      const subscription = merge(
        stakedTokens$,
        ownedTokens$,
        rewardPerTokenRate$,
        rewardForCollection$,
        account$,
        rewardDuration$,
      ).subscribe();
      return () => subscription.unsubscribe();
    }
  }, [account, selectedCollection]);

  const isGhostOG = (val: string): boolean => {
    return val === GOG_ADDRESS;
  };

  // State change functions
  const OnSelectedCollectionChanged = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    e.preventDefault();
    const collection: CollectionType = Collections.filter(
      (col: CollectionType) => col.collectionName === e.target.value,
    )[0];

    SetSelectedCollection(collection);
  };

  // Logic
  const Stake = () => {
    try {
      web3Service.stakeAll(
        selectedCollection.collectionAddress,
        isGhostOG(selectedCollection.collectionAddress),
        selectedTokens,
      );
    } catch (err) {
      console.log(err);
    }
  };

  const WithdrawAll = () => {
    try {
      web3Service.withdrawAll(
        selectedCollection.collectionAddress,
        isGhostOG(selectedCollection.collectionAddress),
      );
    } catch (err) {
      console.log(err);
    }
  };

  const ClaimReward = () => {
    try {
      web3Service.claimRewards(isGhostOG(selectedCollection.collectionAddress));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Stack gap={"20px"}>
      <Container
        p={"50px"}
        pl={"100px"}
        pr={"100px"}
        backdropFilter={"blur(10px)"}
        borderRadius={"20px"}
      >
        <Select
          placeholder="Select Collection"
          onChange={OnSelectedCollectionChanged}
          defaultValue={Collections[0].collectionName}
        >
          {Collections.map((e: CollectionType, index: number) => {
            return (
              <option key={index} value={e.collectionName}>
                {e.collectionName}
              </option>
            );
          })}
        </Select>
        <Stack pt={"40px"} alignItems={"center"} flexDirection={"row"}>
          {selectedCollection ? (
            <>
              <img
                src={
                  isGhostOG(selectedCollection.collectionAddress)
                    ? BooLogo
                    : DrDaoLogo
                }
                height={"50px"}
                width={"140px"}
                alt={"Logo"}
              />
              <Box>
                <Text fontSize={"2em"} fontWeight={"bold"}>
                  {selectedCollection.rewardToken}
                </Text>
                <Text color={"grey"} fontWeight={"bold"}>
                  Reward Token
                </Text>
              </Box>
            </>
          ) : (
            <Box>
              <Text>Select a collection</Text>
            </Box>
          )}
        </Stack>
        {selectedCollection && (
          <Box
            display={"flex"}
            height={"50px"}
            flexDirection="row"
            alignItems={"center"}
            justifyContent={"center"}
            gap={"70px"}
            mt={"50px"}
          >
            <Text fontWeight={"bold"}>
              Staked NFT count:
              <br />
              {stakedNFTs?.length}
            </Text>

            <Text fontWeight={"bold"}>
              Reward Rate: <br />
              {rewardRate}
            </Text>

            <Text fontWeight={"bold"}>
              Owned NFT count:
              <br />
              {myNFTs?.length}
            </Text>
          </Box>
        )}
      </Container>
      <Stack
        pt={"20px"}
        backdropFilter={"blur(10px)"}
        borderRadius={"20px"}
        alignItems={"center"}
      >
        <NFTAlertDialog
          selectedTokens={selectedTokens}
          setSelectedTokens={setSelectedTokens}
          idToNft={idToNft}
          nfts={myNFTs}
          onClick={Stake}
          leastDestructiveRef={leastDestructiveRef}
          isOpen={isOpen}
          onClose={handleClose}
          onOpen={onOpen}
        >
          Stake
        </NFTAlertDialog>
        <Button onClick={ClaimReward} width={"80%"}>
          Claim Reward
        </Button>
        <NFTAlertDialog
          selectedTokens={selectedTokens}
          setSelectedTokens={setSelectedTokens}
          idToNft={idToWithdraw}
          nfts={stakedNFTs}
          onClick={WithdrawAll}
          leastDestructiveRef={leastDestructiveRef}
          isOpen={isWithdrawOpen}
          onClose={handleWithdrawClose}
          onOpen={onWithdrawOpen}
        >
          Withdraw
        </NFTAlertDialog>
      </Stack>
      <Stack
        pt={"20px"}
        backdropFilter={"blur(10px)"}
        borderRadius={"20px"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Stack p={"20px"} gap={"10px"}>
          <Text fontWeight={"bold"}>Rewards:</Text>
          <Text>{rewards}</Text>
          <Text fontWeight={"bold"}>Finish period:</Text>
          <Text>{remainingTime}</Text>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default NFTStakingView;
